const STATUS = Object.freeze({
  UNALLOCATED: "unallocated",
  PARTIALLY_ALLOCATED: "partially_allocated",
  FULLY_ALLOCATED: "fully_allocated",
  OVERALLOCATED: "overallocated",
});

export const CAPITALFLOW_ALLOCATION_STATUS = STATUS;

export function toCents(value) {
  if (value == null || value === "") return 0;

  const normalized =
    typeof value === "string"
      ? value.replace(/[$,\s]/g, "")
      : value;
  const amount = Number(normalized);

  if (!Number.isFinite(amount)) {
    throw new TypeError(`Invalid currency amount: ${value}`);
  }

  return Math.round((amount + Number.EPSILON) * 100);
}

export function fromCents(cents) {
  if (!Number.isSafeInteger(cents)) {
    throw new TypeError("Currency cents must be a safe integer.");
  }
  return cents / 100;
}

function parseStructure(structure) {
  if (Array.isArray(structure)) return structure;
  if (typeof structure !== "string" || !structure.trim()) return [];

  try {
    const parsed = JSON.parse(structure);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function isoDate(value) {
  const text = String(value ?? "").trim();
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

export function aggregatePrincipalMaturities(structure) {
  const byDate = new Map();

  parseStructure(structure).forEach((row) => {
    const maturityDate = isoDate(row?.date ?? row?.maturity_date);
    if (!maturityDate) return;

    const principalCents = toCents(row?.principal ?? 0);
    if (principalCents <= 0) return;

    byDate.set(
      maturityDate,
      (byDate.get(maturityDate) ?? 0) + principalCents
    );
  });

  return [...byDate.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([maturityDate, principalCents]) => ({
      maturityDate,
      principalCents,
      principalAmount: fromCents(principalCents),
    }));
}

export function calculateAllocationStatus({
  principalCents,
  allocatedCents,
  toleranceCents = 0,
}) {
  if (!Number.isSafeInteger(principalCents) || principalCents < 0) {
    throw new TypeError("Principal cents must be a non-negative safe integer.");
  }
  if (!Number.isSafeInteger(allocatedCents) || allocatedCents < 0) {
    throw new TypeError("Allocated cents must be a non-negative safe integer.");
  }
  if (!Number.isSafeInteger(toleranceCents) || toleranceCents < 0) {
    throw new TypeError("Tolerance cents must be a non-negative safe integer.");
  }

  const varianceCents = principalCents - allocatedCents;
  const isFull = Math.abs(varianceCents) <= toleranceCents;
  let status;

  if (isFull) status = STATUS.FULLY_ALLOCATED;
  else if (allocatedCents === 0) status = STATUS.UNALLOCATED;
  else if (varianceCents > 0) status = STATUS.PARTIALLY_ALLOCATED;
  else status = STATUS.OVERALLOCATED;

  return {
    principalCents,
    allocatedCents,
    varianceCents,
    allocationPercentage:
      principalCents === 0
        ? allocatedCents === 0
          ? 0
          : null
        : (allocatedCents / principalCents) * 100,
    status,
  };
}

export function summarizeMaturityAllocations(maturities, allocations) {
  const allocatedByDate = new Map();

  (allocations || []).forEach((allocation) => {
    const maturityDate = isoDate(allocation?.maturityDate ?? allocation?.maturity_date);
    if (!maturityDate) return;
    const amount = allocation?.allocatedCents ?? toCents(allocation?.allocated_amount ?? 0);
    allocatedByDate.set(maturityDate, (allocatedByDate.get(maturityDate) ?? 0) + amount);
  });

  return (maturities || []).map((maturity) => {
    const principalCents =
      maturity.principalCents ?? toCents(maturity.principalAmount ?? maturity.principal_amount);
    const allocatedCents = allocatedByDate.get(maturity.maturityDate) ?? 0;
    return {
      ...maturity,
      ...calculateAllocationStatus({ principalCents, allocatedCents }),
    };
  });
}

export function calculateOutstandingAllocatedPrincipal({
  maturityDate,
  allocatedCents,
  asOfDate,
}) {
  const maturity = isoDate(maturityDate);
  const asOf = isoDate(asOfDate);
  if (!maturity || !asOf) throw new TypeError("Valid maturity and as-of dates are required.");
  if (!Number.isSafeInteger(allocatedCents) || allocatedCents < 0) {
    throw new TypeError("Allocated cents must be a non-negative safe integer.");
  }

  return maturity <= asOf ? 0 : allocatedCents;
}
